import React, { useCallback, useMemo } from "react";
import {
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  Card,
  ListItem,
  SectionHeader,
} from "arkh";

import { FormattedMessage, useIntl } from "react-intl";
import { RoutineControlTypeOption } from "./RoutineControlTypeOption";
import { RoutineEditScreenProps } from "../RoutineEditNavigation";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { Keyboard } from "react-native";
import { useAlert } from "../../../../common/Alert";
import {
  useRoutineEditState,
  useRoutineEditStateSetParams,
} from "../../State/RoutineEditState";

export function RoutineEditControlTypeCard(props: {
  mode: "CREATE" | "EDIT";
  isOrg: boolean;
  routineId: string;
  orgWorkspaceId: string | undefined;
  navigation: RoutineEditScreenProps<"RoutineEditMain">["navigation"];
}) {
  const intl = useIntl();

  const params = useRoutineEditState();
  const setParams = useRoutineEditStateSetParams();
  const navigate = props.navigation.navigate;
  const { connections = [], tasks = [], sections = [], controlType } = params;
  const numberOfTasks = useMemo(() => connections.length, [connections.length]);

  const intl_tokens = useIntlTokens();
  const alert = useAlert();
  const deactivateChecklistAlert = useCallback(
    () =>
      alert.alert(
        intl.formatMessage({
          defaultMessage: "Deactivate Checklist",
          description: "deactive checklist alert title",
        }),
        intl.formatMessage({
          defaultMessage:
            "Remove all tasks before the checklist can be changed.",
          description: "deactivate checklist alert description",
        }),
        [
          {
            style: "default",
            text: intl_tokens.OK,
          },
        ]
      ),
    [alert, intl, intl_tokens.OK]
  );

  const onNoCheckPress = useCallback(() => {
    if (
      props.mode === "CREATE" ||
      controlType !== "checklist" ||
      tasks.length === 0
    ) {
      setParams({
        controlType: "none",
      });
    } else {
      deactivateChecklistAlert();
    }
  }, [
    props.mode,
    controlType,
    tasks.length,
    setParams,
    deactivateChecklistAlert,
  ]);

  const onCheckPress = useCallback(() => {
    if (
      props.mode === "CREATE" ||
      controlType !== "checklist" ||
      tasks.length === 0
    ) {
      setParams({
        controlType: "check",
      });
    } else {
      deactivateChecklistAlert();
    }
  }, [
    props.mode,
    controlType,
    tasks.length,
    setParams,
    deactivateChecklistAlert,
  ]);

  const placeholderData = useMemo(
    () =>
      connections.length === 0 && sections.length === 0
        ? {
            tasks: [
              {
                id: "task1",
                type: "TaskCheck" as const,
                sentiments: ["POSITIVE" as const, "NEGATIVE" as const],
                title: "",
                images: [],
                instructions: "",
              },
            ],
            sections: [
              {
                id: "section1",
                ordinal: 0,
                title: "",
                assignedTeam: null,
                orgTopic: null,
                orgCanBeDisabled: null,
              },
            ],
            selected: {
              type: "TASK" as const,
              taskId: "task1",
              sectionId: "section1",
            } as const,
            connections: [
              {
                taskId: "task1",
                sectionId: "section1",
                ordinal: 0,
                value: 0,
                orgTopic: null,
                canBeDisabled: false,
              },
            ],
          }
        : null,
    [connections.length, sections.length]
  );

  const navigateDeprecatedToChecklist = useCallback(() => {
    Keyboard.dismiss();
    setParams({
      controlType: "checklist",
      tasks: placeholderData?.tasks ?? tasks,
      sections: placeholderData?.sections ?? sections,
      connections: placeholderData?.connections ?? connections,
      selected: placeholderData?.selected ?? params.selected ?? null,
    });
    navigate("RoutineEditChecklist", {
      routineId: props.routineId,
      orgWorkspaceId: props.orgWorkspaceId,
    });
  }, [
    connections,
    navigate,
    params.selected,
    placeholderData?.connections,
    placeholderData?.sections,
    placeholderData?.selected,
    placeholderData?.tasks,
    props.orgWorkspaceId,
    props.routineId,
    sections,
    setParams,
    tasks,
  ]);

  return (
    <>
      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            defaultMessage="Follow up if the routine is completed"
            description="Routine Edit control list item section title"
          />
        </SectionHeader.Title>
      </SectionHeader>
      <Card noSpace defaultBorder>
        {props.isOrg ? null : (
          <Box
            spaceVertical="medium"
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-around",
            }}
          >
            <RoutineControlTypeOption
              title={intl.formatMessage({
                defaultMessage: "No check",
                description:
                  "Routine Edit control list item no check option title",
              })}
              source={require("../../../../assets/images/routineTypes/none.png")}
              selected={controlType === "none"}
              onPress={onNoCheckPress}
            />
            <RoutineControlTypeOption
              title={intl.formatMessage({
                defaultMessage: "Check",
                description:
                  "Routine Edit control list item check option title",
              })}
              source={require("../../../../assets/images/routineTypes/check.png")}
              selected={controlType === "check"}
              onPress={onCheckPress}
            />
            <RoutineControlTypeOption
              title={intl.formatMessage({
                defaultMessage: "Checklist",
                description:
                  "Routine Edit control list item checklist option title",
              })}
              source={require("../../../../assets/images/routineTypes/checklist.png")}
              selected={controlType === "checklist"}
              warn={connections?.length === 0}
              onPress={navigateDeprecatedToChecklist}
            />
          </Box>
        )}
        {controlType === "checklist" || props.isOrg ? (
          <>
            {props.isOrg ? null : <AHSeparator />}
            <RoutineEditChecklistListItem
              numberOfTasks={numberOfTasks}
              onPress={navigateDeprecatedToChecklist}
            />
          </>
        ) : null}
      </Card>
    </>
  );
}

const RoutineEditChecklistListItem = (props: {
  numberOfTasks: number;
  onPress(): void;
}) => {
  const intl_tokens = useIntlTokens();
  return (
    <ListItem
      onPress={props.onPress}
      isLast
      right={
        <>
          {props.numberOfTasks > 0 ? (
            <AHText type="list-item-title" color="text-secondary">
              {props.numberOfTasks}
            </AHText>
          ) : (
            <AHIcon type="issue" color="negative" />
          )}
          <ListItem.NavArrow />
        </>
      }
    >
      <ListItem.Title>{intl_tokens.Tasks}</ListItem.Title>
    </ListItem>
  );
};
